import React from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import { Checkbox as ShadCheckbox } from '@dashboard/components/ui/checkbox';
import { cn } from '@dashboard/lib/utils';
import { OPTION_GROUP_CLASSNAME } from '@dashboard/components/Formik/components/optionGroupClass';

import Label from '@dashboard/components/Form/components/Label';
import LabelDescription from '@dashboard/components/Form/components/LabelDescription';

const propTypes = {
  values: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  inverse: PropTypes.bool,
};

const defaultProps = {
  required: false,
};

const Checkbox = ({
  name,
  className,
  id,
  values,
  required,
  type: _fieldType,
  label: _fieldLabel,
  tooltip: _tooltip,
  helpText: _helpText,
  visible: _visible,
  onChange,
  inverse = false,
  ...props
}) => {
  const [field, , helpers] = useField(name);
  // `inverse` lets a checkbox represent the negation of a boolean field — e.g. a
  // "Hide" checkbox bound to an `enabled` field (checked when enabled is false).
  const checked = inverse ? !field.value : Boolean(field.value);

  return (
    <ShadCheckbox
      id={id || name}
      name={name}
      checked={checked}
      onCheckedChange={value => {
        const isChecked = value === true;
        helpers.setValue(inverse ? !isChecked : isChecked);
        // Optional side-effect hook (e.g. clear a revealed field on uncheck).
        onChange?.(isChecked);
      }}
      required={required}
      className={cn(className)}
      {...props}
    />
  );
};

// Vertical rhythm between checkbox rows lives on the Group, not the row.
// Wrap a list of Checkbox.Wrapper rows in Checkbox.Group to get the standard 8px gap.
const Group = ({ children, className }) => (
  <div className={cn(OPTION_GROUP_CLASSNAME, className)}>{children}</div>
);

const Wrapper = ({ children }) => (
  <div className="flex gap-x-2 w-full items-center">{children}</div>
);

const CheckboxLabel = props => <Label isInline {...props} />;
const CheckboxLabelDescription = props => <LabelDescription {...props} />;

Checkbox.Group = Group;
Checkbox.Wrapper = Wrapper;
Checkbox.Label = CheckboxLabel;
Checkbox.LabelDescription = CheckboxLabelDescription;

Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;

export default Checkbox;
